// Component: CanvasToolbar.jsx
// Purpose: Switch the Strategy Builder Canvas between Flow and Tree and expose Flow-only tools.
import React from 'react';
import { FiGitBranch, FiGrid, FiList, FiSidebar } from 'react-icons/fi';

const VIEW_OPTIONS = [
  { id: 'flow', label: 'Flow', Icon: FiGitBranch },
  { id: 'tree', label: 'Tree', Icon: FiList },
];

//--> WAI-ARIA radio group: one tab stop (the checked radio); arrow keys move AND select, wrapping at the ends.
const handleRadioKeyDown = (event, subView, onSubViewChange, options) => {
  const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
  if (!step) return;
  event.preventDefault();
  const index = options.findIndex(({ id }) => id === subView);
  const next = options[(Math.max(0, index) + step + options.length) % options.length];
  onSubViewChange?.(next.id);
  event.currentTarget.parentElement?.querySelector(`[data-view-id="${next.id}"]`)?.focus();
};

export const CanvasViewSwitch = ({
  subView,
  onSubViewChange,
  options = VIEW_OPTIONS,
  ariaLabel = 'Canvas view',
}) => (
  <div
    role="radiogroup"
    aria-label={ariaLabel}
    className="flex items-center gap-0.5 rounded-md border border-[var(--divider)] bg-bg-2 p-0.5 shadow-sm"
  >
    {options.map(({ id, label, Icon }) => {
      const selected = subView === id;
      return (
        <button
          key={id}
          type="button"
          role="radio"
          aria-checked={selected}
          tabIndex={selected ? 0 : -1}
          data-view-id={id}
          onClick={() => onSubViewChange?.(id)}
          onKeyDown={(event) => handleRadioKeyDown(event, subView, onSubViewChange, options)}
          className={`flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold transition-colors ${selected
            ? 'bg-bg-3 text-[var(--node-visual)] shadow-sm'
            : 'text-fg-3 hover:bg-bg-3 hover:text-fg-1'}`}
        >
          <Icon size={13} aria-hidden="true" />
          <span>{label}</span>
        </button>
      );
    })}
  </div>
);

const CanvasToolbar = ({
  subView = 'flow',
  outlineOpen = false,
  onSubViewChange,
  onToggleOutline,
  onArrange,
}) => (
  <div className="flex items-center gap-1.5">
    <CanvasViewSwitch subView={subView} onSubViewChange={onSubViewChange} />
    {subView === 'flow' && (
      <>
        <button
          type="button"
          aria-label="Outline"
          aria-pressed={outlineOpen}
          onClick={onToggleOutline}
          className={`flex items-center gap-1 rounded border border-[var(--divider)] px-2 py-1 text-xs font-semibold shadow-sm ${outlineOpen
            ? 'bg-bg-3 text-[var(--node-visual)]'
            : 'bg-bg-2 text-fg-3 hover:bg-bg-3 hover:text-fg-1'}`}
        >
          <FiSidebar size={13} aria-hidden="true" />
          Outline
        </button>
        <button
          type="button"
          aria-label="Arrange"
          onClick={onArrange}
          className="flex items-center gap-1 rounded border border-[var(--divider)] bg-bg-2 px-2 py-1 text-xs font-semibold text-fg-3 shadow-sm hover:bg-bg-3 hover:text-fg-1"
        >
          <FiGrid size={13} aria-hidden="true" />
          Arrange
        </button>
      </>
    )}
  </div>
);

export default CanvasToolbar;
